import type { ComponentType } from 'preact'
import type { EntryMeta } from '~/types'

export type CollectionName = 'projects' | 'experiments'

export interface CollectionEntry {
	meta: EntryMeta
	load: () => Promise<{ default: ComponentType }>
}

// Eagerly import only the `meta` named export of every entry (cheap, drives the index
// grids), and lazily import the whole module (the entry body) for on-demand routing.
const projectMetas = import.meta.glob<EntryMeta>('./projects/*.tsx', {
	eager: true,
	import: 'meta',
})
const projectLoaders = import.meta.glob<{ default: ComponentType }>('./projects/*.tsx')

const experimentMetas = import.meta.glob<EntryMeta>('./experiments/*.tsx', {
	eager: true,
	import: 'meta',
})
const experimentLoaders = import.meta.glob<{ default: ComponentType }>('./experiments/*.tsx')

const sources: Record<
	CollectionName,
	{ metas: Record<string, EntryMeta>; loaders: Record<string, () => Promise<{ default: ComponentType }>> }
> = {
	projects: { metas: projectMetas, loaders: projectLoaders },
	experiments: { metas: experimentMetas, loaders: experimentLoaders },
}

const build = (name: CollectionName): CollectionEntry[] => {
	const { metas, loaders } = sources[name]

	return Object.entries(metas)
		.map(([path, meta]) => ({ meta, load: loaders[path] }))
		.sort((a, b) => {
			// Sort by author-defined `order` then name. Featured entries are NOT hoisted,
			// so multi-column tiles stay interspersed with single-column ones on /work.
			const orderA = a.meta.order ?? Number.MAX_SAFE_INTEGER
			const orderB = b.meta.order ?? Number.MAX_SAFE_INTEGER
			if (orderA !== orderB) return orderA - orderB
			return a.meta.name.localeCompare(b.meta.name)
		})
}

const collections: Record<CollectionName, CollectionEntry[]> = {
	projects: build('projects'),
	experiments: build('experiments'),
}

export const getCollection = (name: CollectionName): CollectionEntry[] => collections[name]

export const getEntry = (name: CollectionName, slug: string): CollectionEntry | undefined =>
	collections[name].find((entry) => entry.meta.slug === slug)
